<script setup lang="ts">
import type { CouponPreview } from '~/store/billing'

/**
 * The last screen before the money: what happens next, and anything still
 * missing.
 *
 * Deliberately does NOT restate the price. The sheet's header carries the
 * total — collapsed to one line on every other step, opened on this one — and
 * three statements of one number is how a customer starts wondering which of
 * them the invoice will follow. What this screen adds is the things the summary
 * cannot say: who is being invoiced, the credential for the machine, and the
 * one or two answers still outstanding. What happens AFTER the button is the
 * payment step's to say, and it says it.
 *
 * The access password is here because it is the one thing the draft never
 * writes down — it is the credential for the machine being bought — so it is
 * empty after every sign-in round trip, which is exactly when this screen
 * opens. It used to be an apology on a banner ("we did not keep your
 * password") above a field the customer then had to go and find.
 *
 * Bound to the PAGE's refs, so the field here and the card behind the sheet
 * hold one value, checked against one policy (`~/utils/password`).
 */
const props = withDefaults(
  defineProps<{
    /** Who the invoice will name. The account may be seconds old. */
    customerName?: string | null
    customerEmail?: string | null
    /** Trial terms stay in the main review, including on a collapsed mobile summary. */
    trialDescription?: string | null
    /** Everything still standing between here and the order. */
    blockers?: { key: string; label: string; anchor?: string | null }[]
    requiresPassword?: boolean
    passwordDone?: boolean
    /**
     * Whether this order can carry a coupon at all (a new subscription, not
     * on-demand). The field lives on the page, and the page is behind this
     * sheet — so without offering it here, a customer who registered with a
     * promo code in hand had to dismiss the sheet, scroll to the sidebar, type
     * it and come back. Collapsed by default: most people do not have one.
     */
    couponAllowed?: boolean
    /** A coupon is what is standing in the way — open the field regardless. */
    couponBlocked?: boolean
    couponLoading?: boolean
    couponPreview?: CouponPreview | null
    couponStatus?: string | null
    /** The server refused the order. Stated here, not only as a toast: a toast
     *  behind an open dialog is the "I clicked and nothing happened" this
     *  whole surface exists to remove. */
    orderError?: string | null
  }>(),
  {
    customerName: null,
    customerEmail: null,
    trialDescription: null,
    blockers: () => [],
    requiresPassword: false,
    passwordDone: false,
    couponAllowed: false,
    couponBlocked: false,
    couponLoading: false,
    couponPreview: null,
    couponStatus: null,
    orderError: null,
  },
)

const emit = defineEmits<{
  /** Close the sheet and take the customer to the step that owns this. */
  (e: 'edit-step', anchor: string): void
  (e: 'refresh-coupon'): void
}>()

const password = defineModel<string>('password', { default: '' })
const confirmPassword = defineModel<string>('confirmPassword', { default: '' })
const couponCode = defineModel<string>('couponCode', { default: '' })

/** Asked for, never in the way: a code already typed or already refused opens
 *  it, and so does the customer. */
const couponOpen = ref(false)
const showCoupon = computed(
  () => props.couponAllowed && (couponOpen.value || props.couponBlocked || !!couponCode.value),
)

/** The password and the coupon are asked for HERE; everything else is a step. */
const HANDLED_HERE = new Set(['password', 'passwordMatch', 'coupon', 'signIn', 'trialConsent'])

const blockersElsewhere = computed(() =>
  props.blockers.filter((b) => !HANDLED_HERE.has(b.key)),
)

const invoiceTo = computed(() =>
  [props.customerName, props.customerEmail].filter(Boolean).join(' · '),
)
</script>

<template>
  <div class="space-y-5">
    <!-- Who is being billed. One line, and the cheapest reassurance there is
         when the account was created a moment ago. -->
    <div v-if="invoiceTo" class="flex items-start gap-2 text-sm">
      <UIcon name="i-lucide-receipt-text" class="mt-0.5 size-4 shrink-0 text-dimmed" />
      <div class="min-w-0">
        <span class="text-muted">{{ $t('billing.page.checkout.commit.invoiceTo') }}:</span>
        <span class="ml-1 font-medium">{{ invoiceTo }}</span>
      </div>
    </div>


    <section
      v-if="trialDescription"
      aria-labelledby="commit-trial-terms-title"
      class="space-y-2 rounded-lg border border-default bg-elevated/40 p-4"
    >
      <h3 id="commit-trial-terms-title" class="text-sm font-semibold">
        {{ $t('trial.reviewTitle') }}
      </h3>
      <p id="commit-trial-terms" class="text-sm leading-6 text-default">
        {{ trialDescription }}
      </p>
    </section>

    <!-- ── The access password ──────────────────────────────────────────── -->
    <div v-if="requiresPassword && !passwordDone" class="space-y-2">
      <div>
        <h3 class="text-sm font-semibold">
          {{ $t('billing.page.checkout.commit.passwordTitle') }}
        </h3>
        <p class="mt-0.5 text-xs text-muted">
          {{ $t('billing.page.checkout.commit.passwordWhy') }}
        </p>
      </div>
      <AuthPasswordCreateFields
        v-model:password="password"
        v-model:confirm="confirmPassword"
        password-name="vm-password"
        confirm-name="vm-password-confirm"
      />
    </div>

    <!-- ── A promo code ─────────────────────────────────────────────────── -->
    <UButton
      v-if="couponAllowed && !showCoupon"
      variant="link"
      size="xs"
      class="px-0"
      icon="i-lucide-ticket-percent"
      :label="$t('billing.page.checkout.commit.haveCoupon')"
      @click="couponOpen = true"
    />

    <div v-if="showCoupon" class="space-y-2">
      <UFormField :label="$t('billing.page.checkout.summary.couponField')" name="commit-coupon">
        <div class="flex items-end gap-2">
          <UInput
            v-model="couponCode"
            :placeholder="$t('billing.page.checkout.summary.couponPlaceholder')"
            icon="i-lucide-ticket-percent"
            autocomplete="off"
            autocapitalize="characters"
            class="w-full flex-1"
          />
          <UButton
            variant="outline"
            icon="i-lucide-refresh-cw"
            :loading="couponLoading"
            :disabled="!couponCode"
            @click="emit('refresh-coupon')"
          >
            {{ $t('billing.page.checkout.summary.applyBtn') }}
          </UButton>
        </div>
      </UFormField>
      <!-- A code we will not honour no longer stops the order, so this line
           has to say so: "Coupon not found." alone, beside a working Confirm
           button, reads as "we are still going to take it off". -->
      <p
        v-if="couponStatus"
        role="status"
        class="text-xs"
        :class="couponPreview?.valid ? 'text-success' : 'text-warning'"
      >
        {{ couponStatus }}
        <template v-if="couponPreview && !couponPreview.valid">
          {{ $t('billing.page.checkout.couponStatus.notApplied') }}
        </template>
      </p>
    </div>

    <!-- ── Anything the sheet cannot fix, with a way back to it ─────────── -->
    <div v-if="blockersElsewhere.length" class="space-y-2">
      <h3 class="text-sm font-semibold">
        {{ $t('billing.page.checkout.commit.blockedTitle') }}
      </h3>
      <ul class="space-y-1.5">
        <li
          v-for="blocker in blockersElsewhere"
          :key="blocker.key"
          class="flex items-start justify-between gap-3 text-sm"
        >
          <span class="flex min-w-0 items-start gap-2">
            <UIcon
              name="i-lucide-circle-dashed"
              class="mt-0.5 size-4 shrink-0 text-dimmed"
            />
            <span>{{ blocker.label }}</span>
          </span>
          <UButton
            v-if="blocker.anchor"
            variant="link"
            size="xs"
            class="shrink-0 px-0"
            :label="$t('billing.page.checkout.commit.change')"
            @click="emit('edit-step', blocker.anchor)"
          />
        </li>
      </ul>
    </div>

    <UAlert
      v-if="orderError"
      color="error"
      variant="soft"
      icon="i-lucide-circle-alert"
      :title="$t('billing.page.checkout.commit.orderError')"
      :description="orderError"
    />

    <!-- The one line here, and it earns its place by answering a question this
         screen's own button raises: it says "Confirm and pay" without ever
         asking HOW, so the customer cannot tell whether pressing it commits
         them to a method they have not chosen. It does not — the method is
         picked on the invoice, and stays changeable until that invoice is
         paid.
         What used to sit here instead — that confirming issues an invoice,
         that nothing is charged until it is paid, the invoice → pay → build
         sequence, the currency, the statutory cancellation window — was
         telling somebody holding a total how buying things works. The sidebar
         states the currency, and the very next step opens with "Your invoice
         has been issued. As soon as the payment clears, we start building your
         server." A confirm screen earns its conversions by being short. -->
    <p v-if="!trialDescription" class="text-xs text-muted">
      {{ $t('billing.page.checkout.commit.paymentMethodNote') }}
    </p>
  </div>
</template>
